import { makeStyles } from '@material-ui/core'
import type { CardProps as MuiCardProps } from '@material-ui/core/Card'
import { default as MuiCard } from '@material-ui/core/Card'
import classnames from 'classnames'
import React from 'react'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import type { Action } from '../ActionButton'
import ActionButton from '../ActionButton'
import CustomBadge from '../CustomBadge'
import type { WithUseStyles, StyleClasses } from '../../styles'

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.typography.body1,
    position: 'relative',
    minHeight: theme.typography.pxToRem(35),
    maxWidth: theme.typography.pxToRem(230),
    width: theme.typography.pxToRem(230),
    borderRadius: theme.borderRadius.md,
    background: theme.palette.common.white,
    padding: '1em',
    paddingLeft: 'calc(1em + 7px)',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: '0.5s min-height',
    userSelect: 'none',
    '&:focus:not(:focus-visible)': {
      outline: 'none'
    },
    '&:hover $dragIcon': {
      opacity: 1,
      pointerEvents: 'all'
    },
    '&:after': {
      content: '""',
      borderLeft: `solid ${theme.typography.pxToRem(7)} ${theme.palette.primary.main}`,
      borderColor: ({ borderColor }) => borderColor,
      height: '100%',
      position: 'absolute',
      left: 0
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    justifyContent: 'center',
    minWidth: 0,
    '& label': {
      cursor: 'pointer'
    }
  },
  layout: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: 0
  },
  selected: {
    color: theme.palette.primary.contrastText,
    background: theme.palette.primary.main
  },
  hasThumbnail: {
    // flexDirection: 'column'
    flexDirection: 'row'
  },
  thumbnail: {
    width: theme.typography.pxToRem(24),
    height: theme.typography.pxToRem(32),
    // marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    '& svg': {
      width: '100%',
      height: '100%'
    }
  },
  dense: {
    minHeight: 0,
    padding: '1em 1.5em'
  },
  disabled: {
    background: theme.paintscout.palette.brand.Neutral.P90,
    borderColor: theme.paintscout.palette.brand.Neutral.P50,
    color: theme.paintscout.palette.brand.Neutral.P50,
    pointerEvents: 'none',
    cursor: 'default'
  },
  inactiveBadge: {
    backgroundColor: theme.palette.error.dark
  },
  inactive: {
    background: theme.palette.grey[200],
    color: theme.palette.grey[700]
  },
  dimmed: {
    background: theme.palette.grey[200],
    color: theme.palette.grey[700]
  },
  actionButton: {
    margin: -12,
    color: theme.paintscout.palette.brand.Neutral.P40
  },
  dragIcon: {
    marginLeft: -theme.spacing(1.75),
    color: theme.paintscout.palette.brand.Neutral.P90,
    alignSelf: 'center',
    // drag icon is only visible on hover
    opacity: 0,
    pointerEvents: 'none',
    [theme.breakpoints.down('md')]: {
      opacity: 1,
      pointerEvents: 'all'
    }
  },
  // Applied on top of dragIcon when !draggable (e.g. while a search/filter is active). Keeps the
  // icon's layout box reserved via visibility instead of unmounting it, so the thumbnail/label
  // don't shift left when reordering is temporarily disabled (see #23684).
  dragIconHidden: {
    visibility: 'hidden'
  },
  dashed: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 2px,
          transparent 2px,
          transparent 16px
        )
      `
    }
  },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: theme.borderRadius.sm,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    padding: theme.spacing(0.25, 1),
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(12)
    }
  },
  actionButtonWrapper: {
    marginLeft: 5
  }
}))

export interface TileProps extends WithUseStyles<typeof useStyles>, Omit<MuiCardProps, 'classes'> {
  children?: React.ReactNode
  selected?: boolean
  dense?: boolean
  disabled?: boolean
  active?: boolean
  dimmed?: boolean
  id?: any
  Thumbnail?: React.FunctionComponent
  borderColor?: string
  actions?: Action[]
  draggable?: boolean
  dashed?: boolean
  badge?: {
    label: string
    fill: string
  }
  onActionClick?: (event: React.MouseEvent<HTMLElement>, actionName: string, rowId: string | number) => void
  tileClasses?: StyleClasses<typeof useStyles>
}

const Tile = React.forwardRef(function Tile(props: TileProps, ref) {
  const classes = useStyles({ ...props, classes: { ...props.classes, ...props.tileClasses } })

  const {
    selected: selectedClass,
    dense: denseClass,
    dashed: dashedClass,
    disabled: disabledClass,
    inactive: inactiveClass,
    content: contentClass,
    layout: layoutClass,
    thumbnail: thumbnailClass,
    hasThumbnail: hasThumbnailClass
  } = classes
  const {
    selected,
    dense,
    disabled,
    dashed,
    active,
    dimmed,
    actions,
    badge,

    Thumbnail,
    draggable,
    ...baseCardProps
  } = props

  const cardClasses = [classes.root]
  const inactive = !active && typeof active !== 'undefined'
  if (selected) {
    cardClasses.push(selectedClass)
  } else if (disabled) {
    cardClasses.push(disabledClass)
  } else if (inactive) {
    cardClasses.push(inactiveClass)
  }
  if (dimmed && !disabled && !inactive) {
    cardClasses.push(classes.dimmed)
  }

  if (dense) {
    cardClasses.push(denseClass)
  }
  if (dashed) {
    cardClasses.push(dashedClass)
  }

  const content =
    props.active || typeof props.active === 'undefined' ? (
      props.children
    ) : (
      <CustomBadge message={'This item is inactive and will not be shown.'} classes={{ root: classes.inactiveBadge }}>
        {props.children}
      </CustomBadge>
    )

  function handleClick(event: any) {
    if (props.onClick && !props.disabled) {
      props.onClick(event)
    }
  }
  return (
    <MuiCard
      component="button"
      // @ts-ignore - explicitly set type as 'button' to prevent submitting forms
      type="button"
      {...baseCardProps}
      ref={ref}
      onClick={handleClick}
      classes={{ root: cardClasses.join(' ') }}
      data-testid="tile"
    >
      <div className={classnames({ [layoutClass]: true, [hasThumbnailClass]: !!Thumbnail })}>
        <DragIndicatorIcon
          onClick={(e) => e.stopPropagation()}
          data-drag-indicator="tile"
          className={classnames(classes.dragIcon, { [classes.dragIconHidden]: !draggable })}
        />
        {Thumbnail && (
          <div className={thumbnailClass}>
            <Thumbnail />
          </div>
        )}
        <div className={classnames(layoutClass, 'tile-layout')}>
          <div className={contentClass}>{content}</div>
          {actions && (
            <div className={classes.actionButtonWrapper}>
              <ActionButton
                classes={{
                  button: classes.actionButton
                }}
                // Stable per-row selector for BugBug/E2E: every Tile on a page used to render its
                // ActionButton with the same literal id ("action-button-icon", set inside ActionButton
                // itself), so a test could only find "the 10th one". `props.id` is the item's own key
                // (see TileList's `id={item.key}`), so this is unique and stable per row.
                data-testid={`action-button-${props.id}`}
                onActionClick={(ev, action) => props.onActionClick(ev, action, props.id)}
                disabled={disabled}
                actions={props.actions}
              />
            </div>
          )}
          {badge && (
            <div
              className={classes.badge}
              style={{
                backgroundColor: badge.fill
              }}
            >
              {badge.label}
            </div>
          )}
        </div>
      </div>
    </MuiCard>
  )
})

Tile.defaultProps = {
  color: 'primary',
  dense: false,
  disabled: false
}

export default Tile
